// src/components/analytics/AnalyticsSection.js
// Импорты всегда в начале файла
import React, { useMemo } from 'react';
import DebtAndSurplus from './DebtAndSurplus';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import AdviceBlock from './AdviceBlock';
import {
  calculateIncomeExpenseGrowth,
  calculateRentMortgageGrowth,
} from '../../utils/AnalyticsCalculator';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Импортируем стили (в самом верху)
import './AnalyticsSection.css';

// Регистрируем компоненты и плагин
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const AnalyticsSection = ({ data }) => {
  // Если данные приходят с ключом "data", используем его, иначе берём data напрямую
  const financeData = data && data.data ? data.data : (data || {});
  console.log("AnalyticsSection: financeData =", financeData);

  // Извлекаем объекты по разделам
  const incomeObj = financeData.income || {};
  const expensesObj = financeData.expenses || {};
  const flatObj = financeData.flat || {};
  const rentObj = financeData.rent || {};
  const assetsObj = financeData.assets || {};
  const reserveObj = financeData.reserve || {};
  const yearlyExpensesObj = financeData.yearlyExpenses || {};

  // Период в годах (по умолчанию 120 мес = 10 лет)
  const periodMonths = flatObj.period || 120;
  const periodYears = Math.round(periodMonths / 12);

  // 1) Рост доходов и расходов
  const { incomeValues, expenseValues } = useMemo(() => {
    return calculateIncomeExpenseGrowth(incomeObj, expensesObj, periodYears);
  }, [incomeObj, expensesObj, periodYears]);

  // Вычисляем спред: разница между доходами и расходами
  const spreadValues = useMemo(() => {
    return incomeValues.map((income, index) => income - expenseValues[index]);
  }, [incomeValues, expenseValues]);

  // 2) Сравнение аренды и ипотеки (с учётом роста)
  const { rentGrowthValues, mortgageGrowthValues } = useMemo(() => {
    return calculateRentMortgageGrowth(rentObj, flatObj, periodYears);
  }, [rentObj, flatObj, periodYears]);

  // Итоговые суммы
  const finalIncome = useMemo(() => {
    return Object.values(incomeObj).reduce((acc, val) => (typeof val === 'number' ? acc + val : acc), 0);
  }, [incomeObj]);

  const finalExpenses = useMemo(() => {
    const expSum = Object.values(expensesObj).reduce((acc, val) => (typeof val === 'number' ? acc + val : acc), 0);
    const yearlySum = Object.values(yearlyExpensesObj).reduce((acc, val) => (typeof val === 'number' ? acc + val : acc), 0);
    return expSum + (yearlySum / 12);
  }, [expensesObj, yearlyExpensesObj]);

  const finalAssets = useMemo(() => {
    return Object.values(assetsObj).reduce((acc, val) => (typeof val === 'number' ? acc + val : acc), 0);
  }, [assetsObj]);

  const finalReserve = useMemo(() => {
    return Object.values(reserveObj).reduce((acc, val) => (typeof val === 'number' ? acc + val : acc), 0);
  }, [reserveObj]);

  const finalSpread = useMemo(() => {
    return finalIncome - finalExpenses;
  }, [finalIncome, finalExpenses]);
  
  // Определяем дополнительные переменные для анализа долговых показателей
  const mortgagePayment = flatObj.monthly || 0;
  const debtPayments = mortgagePayment; // Если нет других долгов, используем ипотечный платеж
  const obligatoryExpenses = finalExpenses; // Можно уточнить, если есть выделенные обязательные расходы

  // Метки для линейных графиков (Год 0...Год N)
  const labels = useMemo(() => {
    const arr = [];
    for (let i = 0; i <= periodYears; i++) {
      arr.push(`Год ${i}`);
    }
    return arr;
  }, [periodYears]);

  // ===== ВСТАВКА НОВЫХ ВЫЧИСЛЯЕМЫХ ПЕРЕМЕННЫХ =====

  // Данные для диаграммы окупаемости покупки vs аренда:
  // Вычисляем накопленные ежегодные платежи по ипотеке и аренде (накопленная сумма = ежегодный платеж * год)
  const computedMortgageData = Array.from({ length: periodYears + 1 }, (_, i) =>
    mortgagePayment ? mortgagePayment * 12 * i : 0
  );

  const computedRentData = Array.from({ length: periodYears + 1 }, (_, i) => {
    const monthlyRent = rentObj.rent || 0;
    const growth = rentObj.rentgrowth || 0;
    // Годовой платеж с учётом роста: умножаем базовую сумму на (1 + рост)^i
    return monthlyRent * 12 * Math.pow(1 + growth / 100, i);
  });

  // Данные для графика кэш-флоу: используем итоговые доходы и расходы (предполагается, что это ежемесячные суммы)
  const computedIncomeArray = Array.from({ length: periodYears + 1 }, () => finalIncome * 12);
  const computedExpenseArray = Array.from({ length: periodYears + 1 }, () => finalExpenses * 12);
  const computedPaymentData = Array.from({ length: periodYears + 1 }, () =>
    mortgagePayment ? mortgagePayment * 12 : 0
  );

  // Данные для диаграммы ликвидности активов: рассчитываем процент ликвидных активов
  const totalAssets = finalAssets;
  const liquidAssets = assetsObj.liquid || 0; // Предполагается, что в assetsObj есть поле liquid (если нет, замените на соответствующее)
  const computedLiquidityPercent = totalAssets ? Math.round((liquidAssets / totalAssets) * 100) : 0;

  // Динамика DTI: рассчитываем годовой DTI как (годовой платеж / годовой доход) * 100
  const dtiValues = Array.from({ length: periodYears + 1 }, () => {
    const annualIncome = finalIncome * 12;
    const annualPayment = mortgagePayment ? mortgagePayment * 12 : 0;
    return annualIncome ? Math.round((annualPayment / annualIncome) * 100) : 0;
  });
  const dtiLabels = labels;

  // ===== НАСТРОЙКИ ДИАГРАММ/ГРАФИКОВ =====

  // Ширина столбиков диаграммы
  const barThickness = 16;

  // Опции для столбчатой диаграммы
  const barChartOptions = {
    responsive: true,
    layout: { padding: { top: 20, bottom: 20 } },
    plugins: {
      legend: { display: false },
      title: { display: false },
      // Добавляем настройки для плагина datalabels
      datalabels: {
        anchor: 'end',       // 'end' = ближе к верхушке столбца
        align: 'top',      // 'start' = над верхушкой (можно 'top', 'end' и т.п., поэкспериментируйте)
        offset: 2,          // можно подвинуть подпись чуть выше
        color: '#444',       // цвет текста
        font: {size: 12, family: "'Open Sans', sans-serif", weight: 'bold'},
        formatter: (value) => {
          // Форматируем число с разделением тысяч и добавляем, например, ₽
          // Либо меняйте на нужную вам валюту
          return Math.round(value).toLocaleString('ru-RU');
        },
      },
    },
    scales: {
      x: {
        ticks: { color: 'black', font: { size: 12, family: "'Open Sans', sans-serif" } },
        grid: { display: false },
      },
      y: {
        ticks: { color: 'black', font: { size: 12, family: "'Open Sans', sans-serif" } },
        grid: { color: '#f0f0f0' },
      },
    },
  };
  
  // Опции для линейных графиков
  const lineChartOptions = {
    responsive: true,

    interaction: {
      mode: 'index',
      intersect: false, 
    },
    plugins: {
      tooltip: {
        enabled: true,
        // можно настроить callbacks, стили и пр.
      },
    },
    // maintainAspectRatio: false,
    layout: { padding: { top: 20, bottom: 20 } },
    plugins: {
      legend: {
        position: 'right',
        align: 'start',
        labels: {
          color: 'black',
          usePointStyle: true,       // квадратики/кружочки вместо прямоугольников
          pointStyle: 'rectRounded', // скругленные квадратики
          boxWidth: 15,
          padding: 20,              // отступ между легендой и графиком
          font: { size: 12, style: 'normal', weight: 'normal', family: "'Open Sans', sans-serif"},
        },
      },
      title: { display: false },
      datalabels: {
        display: false, // Отключаем подписи для линий
    },
  },
    scales: {
      x: {
        ticks: { color: 'black', font: { size: 10, family: "'Open Sans', sans-serif"} },
        grid: { display: false },
      },
      y: {
        ticks: { color: 'black', font: { size: 12, family: "'Open Sans', sans-serif"} },
        grid: { color: '#f0f0f0' },
      },
    },
    elements: {
      line: {
        borderWidth: 3,
        tension: 0.3,
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
      },
      point: {
        radius: 3,
        borderWidth: 2,
        hoverRadius: 7,
      },
    },
  };

  // Опции для групповой столбчатой диаграммы (с легендой)
  const groupedBarChartOptions = {
    responsive: true,
    // maintainAspectRatio: false,
    layout: { padding: { top: 20, bottom: 20 } },
    plugins: {
      legend: {
        display: false,
        position: 'right',
        align: 'start',
        labels: {
          color: 'black',
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 15,
          padding: 20,
          font: { size: 12, style: 'normal', weight: 'normal', family: "'Open Sans', sans-serif"},
        },
      },
      title: { display: false },

      // ВАЖНО: Добавляем секцию datalabels
      datalabels: {
        anchor: 'end',   // Подпись у верхушки столбца
        align: 'top',    // Располагаем её над вершиной столбика
        offset: 2,       // Чуть сместим подпись, чтобы не налезала на столбик
        color: '#444',   // Цвет текста
        font: {size: 12, family: "'Open Sans', sans-serif", weight: 'bold'},
        formatter: (value) => {
          // Форматирование числа (разделение тысяч) и добавление "₽"
          return Math.round(value).toLocaleString('ru-RU');
        },
      },
    },
    scales: {
      x: {
        ticks: { color: 'black', font: { size: 12, family: "'Open Sans', sans-serif"} },
        grid: { display: false },
      },
      y: {
        ticks: { color: 'black', font: { size: 12, family: "'Open Sans', sans-serif"} },
        grid: { color: '#f0f0f0' },
      },
    },
  };

  // ======== 2) "Анализ окупаемости покупки vs аренда" (накопительные платежи) ========
  // Предположим, flatObj.credit - это основная сумма (тело),
  // flatObj.sumpercent - общая сумма процентов (упрощённо, или рассчитана заранее).
  // rentObj.rent - аренда/месяц, rentObj.rentgrowth - рост аренды (%).
  // Здесь показываем 3 линии: "Накопленное тело", "Накопленные проценты", "Накопленная аренда".
  
  const creditBody = flatObj.credit || 0;
  const creditInterest = flatObj.sumpercent || 0;

  const bodyAnnual = creditBody / (periodYears || 1);
  const interestAnnual = creditInterest / (periodYears || 1);

  let cumulativeBody = 0;
  let cumulativeInterest = 0;
  let cumulativeRent = 0;

  const cumulativeBodyArr = [];
  const cumulativeInterestArr = [];
  const cumulativeMortgageTotalArr = []; // Новая линия: тело + проценты
  const cumulativeRentArr = [];

// Объявляем переменные для использования в цикле накопления, чтобы "Год 0" было 0
const monthlyRentValue = rentObj.rent || 0;
const growthValue = rentObj.rentgrowth || 0;

for (let i = 0; i <= periodYears; i++) {
  let rentThisYear = 0;
  // Платёж начинаем считать только со второго шага (i=1).
  // Для i = 0 значение останется 0
  if (i > 0) {
    // Используем (i - 1) для расчёта, чтобы в Год 1
    // был платеж за первый год (без учета предыдущих выплат)
    rentThisYear = monthlyRentValue * 12 * Math.pow(1 + growthValue / 100, i - 1);
  }

    // Начисляем платежи (в Год 0 платежей нет)
    cumulativeBody += i === 0 ? 0 : bodyAnnual;
    cumulativeInterest += i === 0 ? 0 : interestAnnual;
    cumulativeRent += rentThisYear;

    cumulativeBodyArr.push(Math.round(cumulativeBody));
    cumulativeInterestArr.push(Math.round(cumulativeInterest));
    cumulativeMortgageTotalArr.push(Math.round(cumulativeBody + cumulativeInterest));
    cumulativeRentArr.push(Math.round(cumulativeRent));
  }

  const dataPaybackComparison = {
    labels,
    datasets: [
      {
        label: 'Ипотека всего',
        data: cumulativeMortgageTotalArr,
        borderColor: '#4BC0C0',
        backgroundColor: 'rgba(78, 114, 213, 0.2)',
        pointBackgroundColor: '#4BC0C0',
        fill: false,
      },
      {
        label: 'Проценты',
        data: cumulativeInterestArr,
        borderColor: '#FF6384',
        backgroundColor: 'rgba(255,99,132,0.2)',
        pointBackgroundColor: '#FF6384',
        fill: false,
      },
      {
        label: 'Тело кредита',
        data: cumulativeBodyArr,
        borderColor: '#36A2EB',
        backgroundColor: 'rgba(54,162,235,0.2)',
        pointBackgroundColor: '#36A2EB',
        fill: false,
      },
      {
        label: 'Аренда',
        data: cumulativeRentArr,
        borderColor: '#FFCE56',
        backgroundColor: 'rgba(255,206,86,0.2)',
        pointBackgroundColor: '#FFCE56',
        fill: false,
      },
    ],
  };

  // ===== ДАННЫЕ ДЛЯ ДИАГРАММ/ГРАФИКОВ =====

// Столбчатая диаграмма итоговых сумм

const finalChartLabels = ["Доходы", "Расходы", "Разница (спред)"];
const finalChartValues = [finalIncome, finalExpenses, finalSpread];


const dataFinalSums = {
  labels: finalChartLabels,
  datasets: [
    {
      label: "", 
      data: finalChartValues,
      backgroundColor: [
        '#36A2EB', // Доходы
        '#FF6384', // Расходы
        '#4BC0C0', // Разница
      ],
      borderRadius: 10,
      borderSkipped: false,
      borderWidth: 0,
      barThickness,
      maxBarThickness: barThickness,
    },
  ],
};


  // Линейный график роста доходов и расходов
  const dataIncomeExpense = {
    labels,
    datasets: [
      {
        label: 'Доходы',
        data: incomeValues,
        borderColor: '#36A2EB',
        backgroundColor: 'rgba(54,162,235,0.2)',
        pointBackgroundColor: '#36A2EB',
      },
      {
        label: 'Расходы',
        data: expenseValues,
        borderColor: '#FF6384',
        backgroundColor: 'rgba(255,99,132,0.2)',
        pointBackgroundColor: '#FF6384',
      },
      {
        label: 'Спред',
        data: spreadValues,
        borderColor: '#4BC0C0', // Например, зелёный цвет
        backgroundColor: 'rgba(0,160,0,0.2)',
        pointBackgroundColor: '#4BC0C0',
      },
    ],
  };

// 3) Групповая столбчатая диаграмма "Сравнение ежемесячных затрат на квартиру"
const mortgageCredit = flatObj.credit || 0;
const mortgageInterest = flatObj.sumpercent || 0;

// Суммарная аренда за весь период (учёт роста rentgrowth)
const finalRent = useMemo(() => {
  let cumulative = 0;
  const monthlyRent = rentObj.rent || 0;
  const growth = rentObj.rentgrowth || 0;
  for (let i = 0; i <= periodYears; i++) {
    cumulative += monthlyRent * 12 * Math.pow(1 + growth / 100, i);
  }
  return cumulative;
}, [rentObj, periodYears]);

// Считаем общую сумму ипотечных платежей (тело + проценты)
const mortgageTotal = mortgageCredit + mortgageInterest;

// Новый набор меток: четыре отдельные колонки
const costComparisonLabels = [
  "Ипотека всего",
  "Тело кредита",
  "Проценты",
  "Аренда",
];

// Новый объект dataCostComparison с одним набором данных
const dataCostComparison = {
  labels: costComparisonLabels,
  datasets: [
    {
      label: '',
      data: [mortgageTotal, mortgageCredit, mortgageInterest, finalRent],
      // Каждая колонка будет иметь свой цвет
      backgroundColor: [
        '#4BC0C0',  // Ипотека всего
        '#36A2EB',  // Тело кредита
        '#FF6384',  // Проценты
        '#FFCE56',  // Аренда
      ],
      borderRadius: 10,
      borderSkipped: false,
      barThickness: barThickness,
      maxBarThickness: barThickness,
    },
  ],
};

  // Линейный график сравнения аренды и ипотеки (с учетом роста)
  const dataRentMortgageGrowth = {
    labels,
    datasets: [
      {
        label: 'Ипотека',
        data: mortgageGrowthValues,
        borderColor: '#4BC0C0',
        backgroundColor: 'rgba(75,192,192,0.2)',
        pointBackgroundColor: '#4BC0C0',
      },
      {
        label: 'Аренда',
        data: rentGrowthValues,
        borderColor: '#FFCE56',
        backgroundColor: 'rgba(255,206,86,0.2)',
        pointBackgroundColor: '#FFCE56',
      },
    ],
  };

  return (
    <div className="analyticsContainer">
      {/* 1) Столбчатая диаграмма "Итоговые суммы" */}
      <div className="chartContainer">
        <h3 className="chartTitle">Итоговые ежемесячные суммы (доходы, расходы и спред)</h3>
        <p className="chartDescription">Соотношение доходов, расходов и их разницы (спред).
        Учитываются ежемесячные расходы + годовые (делённые на 12)</p>
        <div className="canvasContainer">
          <Bar data={dataFinalSums} options={barChartOptions} />
        </div>
      </div>
  
      {/* 2) Линейный график "График роста доходов и расходов" */}
      <div className="chartContainer">
        <h3 className="chartTitle">График роста доходов и расходов</h3>
        <p className="chartDescription">График учитывает рост доходов и расходов с учетом годового роста, учтенного в соответствующих разделах, а также сред (разницу расходов и доходов)</p>
        <div className="canvasContainer">
          <Line data={dataIncomeExpense} options={lineChartOptions} />
        </div>
      </div>
  
      {/* 3) Групповая столбчатая диаграмма "Сравнение итоговых затрат" */}
      <div className="chartContainer">
        <h3 className="chartTitle">Сравнение итоговых затрат на кредит и аренду</h3>
        <p className="chartDescription">График показывает соотношение итоговых платежей по кредиту и аренде за весь срок кредита</p>
        <div className="canvasContainer">
          <Bar data={dataCostComparison} options={groupedBarChartOptions} />

        </div>
      </div>
  
      {/* Сравнение аренды и ипотеки (ежегодные платежи, без накопления) */}
      <div className="chartContainer">
        <h3 className="chartTitle">Сравнение аренды и ипотеки (год к году)</h3>
        <p className="chartDescription">График показывает сравнение затрат год к году с учётом роста арендных платежей</p>
        <div className="canvasContainer">
          <Line data={dataRentMortgageGrowth} options={lineChartOptions} />
        </div>
      </div>
  
      {/* Анализ окупаемости покупки vs аренда (накопительно) */}
      <div className="chartContainer">
        <h3 className="chartTitle">Сравнение затрат накопительно за весь срок</h3>
        <p className="chartDescription">
          Здесь показаны накопленные платежи за весь срок кредита: <br />
          «ипотечный платеж», «тело кредита», «проценты» и «аренда» за весь срок кредита для сравнения.
        </p>
        <div className="canvasContainer">
          <Line data={dataPaybackComparison} options={lineChartOptions} />
        </div>
      </div>
  
      {/* Блок финансовой аналитики (DebtAndSurplus) */}
      <div className="chartContainer">
        <h3 className="chartTitle">Финансовая аналитика</h3>
        <p className="chartDescription">
          DTI (от англ. debt-to-income ratio) — это коэффициент соотношения долга к доходу, который измеряет общие ежемесячные выплаты по долгу человека по отношению к его ежемесячному доходу. 
          Он помогает кредиторам оценить способность заёмщика управлять ежемесячными платежами и погашать долги.
          Банки рекомендуют держать DTI ниже 36%.
        </p>
        <DebtAndSurplus
          monthlyIncome={finalIncome}
          debtPayments={flatObj.monthly || 0}
          mortgagePayment={flatObj.monthly || 0}
          obligatoryExpenses={finalExpenses}
        />
      </div>  
      {/* <AdviceBlock /> */}
    </div>
  );
};

export default AnalyticsSection;